import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "Intervenez-vous en urgence pour les fuites de toiture ?",
      answer: "Oui, nous assurons un service d'urgence 7j/7 pour toute fuite de toiture sur Treillières et la Loire-Atlantique. En cas de sinistre (tempête, tuiles envolées, fuite importante), nous intervenons sous 2h pour sécuriser votre habitation et limiter les dégâts. Contactez-nous directement au 06.46.70.26.17."
    },
    {
      question: "Quelles aides financières pour la rénovation de ma toiture ?",
      answer: "Plusieurs aides sont disponibles pour vos travaux de toiture en Loire-Atlantique : MaPrimeRénov' pour l'isolation, Prime CEE (Certificats d'Économie d'Énergie), TVA réduite pour l'isolation, et éco-prêt à taux zéro. Nous vous accompagnons dans vos démarches pour bénéficier de ces aides."
    },
    {
      question: "Quelle est la durée de vie d'une toiture en tuiles ?",
      answer: "Une toiture en tuiles terre cuite bien entretenue dure entre 50 et 100 ans selon la qualité des matériaux et les conditions climatiques de Loire-Atlantique. Les tuiles béton ont une durée de vie de 30 à 50 ans. Nous recommandons un entretien tous les 2 ans (démoussage, traitement hydrofuge) pour préserver votre couverture."
    },
    {
      question: "Faut-il un permis de construire pour refaire sa toiture à Treillières ?",
      answer: "Pour une rénovation à l'identique (même matériau, même couleur, même forme), une simple déclaration préalable de travaux suffit. Un permis de construire est nécessaire si vous modifiez l'aspect extérieur (changement de matériau, surélévation, création de Velux). Nous vous accompagnons dans vos démarches administratives auprès de la mairie de Treillières."
    },
    {
      question: "Combien de temps durent les travaux de toiture ?",
      answer: "La durée varie selon l'ampleur du chantier : 2-3 jours pour une réparation simple, 5-7 jours pour une réfection partielle, et 2-3 semaines pour une rénovation complète avec isolation et charpente. Nous établissons un planning précis lors du devis et nous engageons sur les délais annoncés."
    },
    {
      question: "Proposez-vous une garantie sur vos travaux de couverture ?",
      answer: "Oui, tous nos travaux sont couverts par la garantie décennale obligatoire qui protège pendant 10 ans les dommages compromettant la solidité de l'ouvrage. Nous proposons également une garantie biennale sur les équipements (gouttières, Velux) et une garantie de parfait achèvement d'un an couvrant tous les désordres signalés."
    },
    {
      question: "Quand faut-il faire démousser sa toiture en Loire-Atlantique ?",
      answer: "Le climat humide de la Loire-Atlantique favorise la prolifération de mousses et lichens. Nous recommandons un démoussage tous les 2-3 ans, idéalement au printemps ou en automne. Un traitement hydrofuge appliqué après le démoussage protège votre toiture pendant 5-10 ans et préserve l'étanchéité des tuiles ou ardoises."
    }
  ];

  // Structured data for FAQ
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="py-24 bg-gradient-to-b from-background-gray to-background relative overflow-hidden">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-2 bg-primary/10 text-primary font-semibold rounded-full mb-6 text-sm uppercase tracking-wider animate-bounce-in">
            Foire Aux Questions
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-primary animate-fade-in">
            Questions Fréquentes sur la Couverture
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Tout ce que vous devez savoir sur la rénovation et l'entretien de toiture à Treillières
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-card rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover-lift"
              data-animate="fade-in"
              data-animate-delay={index * 100}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between gap-4 hover:bg-accent/5 transition-colors"
                aria-expanded={openIndex === index}
              >
                <h3 className="text-lg md:text-xl font-bold text-primary pr-4">
                  {faq.question}
                </h3>
                <ChevronDown
                  className={cn(
                    "h-6 w-6 text-accent flex-shrink-0 transition-transform duration-300",
                    openIndex === index && "rotate-180"
                  )}
                />
              </button>
              <div
                className={cn(
                  "px-8 overflow-hidden transition-all duration-300",
                  openIndex === index ? "max-h-96 py-6" : "max-h-0"
                )}
              >
                <p className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-lg text-muted-foreground mb-6">
            Vous avez d'autres questions ?
          </p>
          <a
            href="tel:0646702617"
            className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-white font-bold rounded-xl hover:bg-accent/90 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Appelez-nous : 06.46.70.26.17
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQ;